import React from "react";
import { Layers } from "lucide-react";
import type { MapFeatureCollection } from "../../api/map";
import { LANDMARK_CATEGORIES } from "../../lib/landmark-icons";

export type FilterType = "all" | "peaks" | "trails" | "landmarks" | "gaps" | "completed" | "none";

interface FilterBarProps {
  filter: FilterType;
  onChange: (f: FilterType) => void;
  features: MapFeatureCollection | null;
  /** Landmark categories currently shown - drives the tab counts. Toggles
   * live in the Map layers sheet (ATL-106). */
  landmarkCategories: string[];
  /** Opens the Map layers sheet. Omitted on static/showcase maps. */
  onOpenLayers?: () => void;
  /** Any layer (collection or landmark category) is currently hidden. */
  layersActive?: boolean;
}

const pillBtnStyle = (on: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: 7,
  padding: "0 14px",
  height: 34,
  flex: "0 0 auto",
  borderRadius: "var(--radius-pill)",
  cursor: "pointer",
  border: "none",
  fontFamily: "var(--font-sans)",
  fontSize: 13,
  fontWeight: 600,
  background: on ? "var(--accent)" : "transparent",
  color: on ? "var(--text-on-gold)" : "var(--text-secondary)",
  boxShadow: on ? "var(--glow-gold-sm)" : "none",
  transition: "var(--t-colors)",
});

function CountBadge({ count, on }: { count: number; on: boolean }) {
  return (
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10.5,
        fontWeight: 700,
        padding: "1px 6px",
        borderRadius: "var(--radius-pill)",
        background: on ? "rgba(0,0,0,.16)" : "var(--surface-sunken)",
        color: on ? "var(--text-on-gold)" : "var(--text-muted)",
      }}
    >
      {count}
    </span>
  );
}

export function FilterBar({
  filter,
  onChange,
  features,
  landmarkCategories,
  onOpenLayers,
  layersActive,
}: FilterBarProps) {
  const f = features?.features ?? [];
  const catSet = new Set(landmarkCategories);
  const allCatsOn = landmarkCategories.length >= LANDMARK_CATEGORIES.length;

  // Counts reflect what the map will actually show — landmarks in deselected
  // categories are excluded from All/Landmarks/Gaps/Completed too. (Hidden
  // collections are already filtered out of `features` by MapPage.)
  const visible = allCatsOn
    ? f
    : f.filter((x) => x.properties.type !== "landmark" || catSet.has(x.properties.category ?? ""));
  const counts: Partial<Record<FilterType, number>> = {
    all: visible.length,
    peaks: f.filter((x) => x.properties.type === "peak").length,
    trails: f.filter((x) => x.properties.type === "trail").length,
    landmarks: visible.filter((x) => x.properties.type === "landmark").length,
    gaps: visible.filter((x) => x.properties.status === "not_collected").length,
    completed: visible.filter((x) => x.properties.status === "collected").length,
  };

  const tabs: [FilterType, string][] = [
    ["all", "All"],
    ["peaks", "Peaks"],
    ["trails", "Trails"],
    ["landmarks", "Landmarks"],
    ["gaps", "Gaps"],
    ["completed", "Completed"],
    ["none", "Hide"],
  ];

  return (
    <div style={{ position: "relative", minWidth: 0, maxWidth: "100%" }}>
      <div
        className="r-scroll-x"
        style={{
          display: "flex",
          gap: 6,
          padding: 5,
          borderRadius: "var(--radius-pill)",
          background: "var(--surface-overlay)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: "1px solid var(--border-default)",
          boxShadow: "var(--shadow-md)",
        }}
      >
        {tabs.map(([id, label]) => {
          const on = filter === id;
          return (
            <button key={id} onClick={() => onChange(id)} style={pillBtnStyle(on)}>
              {label}
              {counts[id] != null && <CountBadge count={counts[id]!} on={on} />}
            </button>
          );
        })}

        {/* Map layers sheet trigger (ATL-106) — replaces the old
            landmark-category dropdown, which folded into the sheet. */}
        {onOpenLayers && (
          <button
            onClick={onOpenLayers}
            title="Map layers"
            aria-label="Map layers"
            style={{
              ...pillBtnStyle(false),
              padding: "0 11px",
              color: layersActive ? "var(--gold-400)" : "var(--text-secondary)",
              position: "relative",
            }}
          >
            <Layers size={15} />
            {layersActive && (
              <span
                style={{
                  position: "absolute",
                  top: 7,
                  right: 6,
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: "var(--gold-400)",
                }}
              />
            )}
          </button>
        )}
      </div>
    </div>
  );
}
